import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PatientDialog } from "@/components/opd/PatientDialog";
import { PatientRecordDialog } from "@/components/opd/PatientRecordDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, Pencil, Eye, Loader2, UserPlus, MessageSquare } from "lucide-react";

export default function Patients() {
  const { has } = useFacility();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [recordId, setRecordId] = useState(null);
  const [unread, setUnread] = useState({});

  const loadUnread = () => api.get("/opd/message-unread").then((r) => setUnread(r.data || {})).catch(() => {});
  useEffect(() => { loadUnread(); const iv = setInterval(loadUnread, 15000); return () => clearInterval(iv); }, []);

  const load = () => {
    setRows(null);
    const params = new URLSearchParams({ limit: "100" });
    if (q) params.set("search", q);
    if (typeFilter !== "all") params.set("type_filter", typeFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/opd/patients?${params.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, typeFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setEditing(null);
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const viewDetail = (p) => setRecordId(p.id);

  return (
    <div data-testid="opd-patients-page">
      <PageHeader title="Patients" subtitle="Clinic-owned patient registry for this facility's organisation."
        actions={has("patient:create") && <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-blue-700 hover:bg-blue-800" data-testid="register-patient-button"><Plus className="h-4 w-4 mr-1.5" /> Register Patient</Button>} />

      <div className="relative mb-3 max-w-md">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input className="pl-8 bg-white" placeholder="Search by name, mobile or UHID" value={q} onChange={(e) => setQ(e.target.value)} data-testid="patient-search-input" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="patient-type-filter">
          {["all", "opd", "ipd"].map((t) => (
            <button key={t} onClick={() => setTypeFilter(t)} data-testid={`filter-type-${t}`}
              className={`px-3 py-1.5 text-sm transition-colors ${typeFilter === t ? "bg-blue-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {t === "all" ? "All" : t.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-slate-500">From</span>
          <Input type="date" className="h-9 w-auto bg-white" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          <span className="text-slate-500">To</span>
          <Input type="date" className="h-9 w-auto bg-white" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
        </div>
        {(typeFilter !== "all" || dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={() => { setTypeFilter("all"); setDateFrom(""); setDateTo(""); }} data-testid="filter-clear">Clear</Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>UHID</TableHead><TableHead>Name</TableHead><TableHead>Gender / Age</TableHead><TableHead>Mobile</TableHead><TableHead>Type</TableHead><TableHead>Blood</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`patient-row-${p.id}`}>
                  <TableCell className="font-mono text-xs text-blue-700 font-semibold">{p.uhid}</TableCell>
                  <TableCell className="font-medium text-slate-800">
                    <span className="inline-flex items-center gap-2">{p.name}
                      {unread[p.id] > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-600 text-white rounded-full px-2 py-0.5" data-testid={`patient-unread-${p.id}`}><MessageSquare className="h-3 w-3" />{unread[p.id]}</span>}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{p.gender}, {p.age ?? "—"}y</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.mobile || "—"}</TableCell>
                  <TableCell data-testid={`patient-type-${p.id}`}>
                    <div className="flex gap-1">
                      {p.has_opd && <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">OPD</span>}
                      {p.has_ipd && <span className="text-[10px] font-semibold bg-teal-100 text-teal-700 rounded px-1.5 py-0.5">IPD</span>}
                      {!p.has_opd && !p.has_ipd && <span className="text-slate-300">—</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{p.blood_group || "—"}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`patient-actions-${p.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => viewDetail(p)} data-testid={`patient-view-${p.id}`}><Eye className="h-4 w-4 mr-2" /> View records</DropdownMenuItem>
                        {has("patient:edit") && <DropdownMenuItem onClick={() => { setEditing(p); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>}
                        {has("opd:create") && <DropdownMenuItem onClick={() => navigate(`/app/opd?patient=${p.id}`)} data-testid={`patient-newvisit-${p.id}`}><UserPlus className="h-4 w-4 mr-2" /> New visit (queue)</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-12">No patients found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>

      <PatientDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={(p) => {
        load();
        if (!editing && p?.id && has("opd:create")) navigate(`/app/opd?patient=${p.id}`);
      }} />

      <PatientRecordDialog open={!!recordId} patientId={recordId} onOpenChange={() => { setRecordId(null); loadUnread(); }} has={has} />
    </div>
  );
}
